import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, IconButton, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useOutletContext } from "react-router";
import SectionLoader from "../../../../../../../../component/SectionLoader";
import EmptyState from "../../../../../../../../component/EmptyState";
import StatementCard from "../../../../../../../../component/leave/StatementCard";
import { StatusChip, headCellSx, bodyCellSx } from "../../../../../../../../component/listing";
import { toneColors } from "../../../../../../../../component/statusTone";
import {
  getLeaveRequestsByEmployee,
  getAllLeaveType,
  getLeaveStatement,
} from "../../../../../../../../services/leave";
import { tokens, radius } from "../../../../../../../../theme";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const REQUEST_TONE = { Pending: "warning", Approved: "success", Rejected: "danger" };

// How each ledger movement reads to a human. The stored `type` is the source of
// truth; this is only its label.
const MOVEMENT = {
  ACCRUAL: { label: "Accrual", detail: "Earned this period", tone: "success" },
  LEAVE_TAKEN: { label: "Leave taken", detail: "Approved leave", tone: "danger" },
  ENCASHMENT: { label: "Encashment", detail: "Paid out", tone: "warning" },
  ADJUSTMENT: { label: "Adjustment", detail: "Manual or lapse", tone: "muted" },
};

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
const num = (v) =>
  v === null || v === undefined
    ? "—"
    : Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const Dash = () => <Box component="span" sx={{ color: tokens.textPlaceholder }}>—</Box>;

const ProfileLeave = () => {
  const [, employeeId] = useOutletContext();
  const today = useMemo(() => new Date(), []);

  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [scope, setScope] = useState("month"); // "month" | "year"
  const [statement, setStatement] = useState(null);
  const [requests, setRequests] = useState([]);
  const [typeById, setTypeById] = useState({});
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const requestId = useRef(0);
  const { year, month } = cursor;

  const fetchData = useCallback(async () => {
    if (!employeeId) return;
    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);
    setFailed(false);

    const [stmt, lr, types] = await Promise.all([
      getLeaveStatement(employeeId, { year, month: scope === "month" ? month : undefined }),
      getLeaveRequestsByEmployee(employeeId),
      getAllLeaveType(),
    ]);
    if (id !== requestId.current) return;

    if (stmt?.success === false) setFailed(true);
    setStatement(stmt?.data || null);
    setRequests(Array.isArray(lr?.data) ? lr.data : []);
    const m = {};
    (types?.data || []).forEach((t) => { m[t._id] = t.name; });
    setTypeById(m);
    setLoading(false);
  }, [employeeId, year, month, scope]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setSelectedType(null); }, [year, month, scope]);

  const step = (delta) =>
    setCursor((c) => {
      if (scope === "year") return { ...c, year: c.year + delta };
      const next = new Date(Date.UTC(c.year, c.month + delta, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() };
    });

  // stable identity: the `|| []` fallback would otherwise be a fresh array each
  // render and defeat the memo below
  const types = useMemo(() => statement?.types || [], [statement]);

  // one ledger, ordered as the API returned it, optionally narrowed to a type
  const entries = useMemo(() => {
    const picked = selectedType ? types.filter((t) => String(t.leaveType.id) === selectedType) : types;
    return picked
      .flatMap((t) => t.entries.map((e) => ({ ...e, typeName: t.leaveType.name })))
      // application order, matching the API — the balance column only makes
      // sense in the order the movements were actually applied
      .sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
  }, [types, selectedType]);

  const periodLabel = scope === "year" ? `${year}` : `${MONTHS[month]} ${year}`;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton aria-label="Previous" size="small" onClick={() => step(-1)} sx={{ border: `1px solid ${tokens.border}` }}>
            <ChevronLeftIcon sx={{ fontSize: "1.125rem" }} />
          </IconButton>
          <Box sx={{ fontSize: "1rem", fontWeight: 600, minWidth: "9rem", textAlign: "center" }}>
            {periodLabel}
          </Box>
          <IconButton aria-label="Next" size="small" onClick={() => step(1)} sx={{ border: `1px solid ${tokens.border}` }}>
            <ChevronRightIcon sx={{ fontSize: "1.125rem" }} />
          </IconButton>
        </Stack>

        <Stack
          direction="row"
          sx={{ border: `1px solid ${tokens.border}`, borderRadius: "999px", p: "4px", background: tokens.card }}
        >
          {["month", "year"].map((s) => (
            <Box
              key={s}
              component="button"
              type="button"
              onClick={() => setScope(s)}
              aria-pressed={scope === s}
              sx={{
                font: "inherit",
                fontSize: "0.8125rem",
                fontWeight: scope === s ? 600 : 500,
                textTransform: "capitalize",
                border: "none",
                cursor: "pointer",
                borderRadius: "999px",
                px: 1.5,
                py: 0.5,
                color: scope === s ? tokens.onPrimary : tokens.textMuted,
                background: scope === s ? tokens.primary : "transparent",
                "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
              }}
            >
              {s}
            </Box>
          ))}
        </Stack>
      </Stack>

      {loading ? (
        <SectionLoader />
      ) : failed ? (
        <EmptyState variant="error" title="Couldn't load leave statement" onRetry={fetchData} />
      ) : (
        <>
          {statement?.reconciles === false && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "flex-start",
                border: `1px solid ${tokens.warning}`,
                background: tokens.warningTint,
                borderRadius: "12px",
                p: "0.75rem 1rem",
                mb: 2,
              }}
            >
              <Box sx={{ fontSize: "0.8125rem", color: tokens.text, lineHeight: 1.5 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>Ledger and balance disagree.</Box>{" "}
                A balance here was changed without a matching movement being recorded, so the
                figures below can't be traced end to end. Every credit and debit should be written
                through the same path that appends this ledger.
              </Box>
            </Stack>
          )}

          {types.length === 0 ? (
            <EmptyState
              title="No leave entitlement"
              description="This employee holds no leave balance and has no ledger history."
            />
          ) : (
            <Stack spacing={1} sx={{ mb: 2 }}>
              {types.map((t) => (
                <StatementCard
                  key={String(t.leaveType.id)}
                  statement={t}
                  active={selectedType === String(t.leaveType.id)}
                  onClick={
                    t.entries.length
                      ? () =>
                          setSelectedType(
                            selectedType === String(t.leaveType.id) ? null : String(t.leaveType.id)
                          )
                      : undefined
                  }
                />
              ))}
            </Stack>
          )}

          <Box
            sx={{
              border: `1px solid ${tokens.border}`,
              borderRadius: `${radius.card}px`,
              background: tokens.card,
              p: "1.25rem",
              mb: 2,
            }}
          >
            <Stack
              direction="row"
              alignItems="baseline"
              justifyContent="space-between"
              sx={{ mb: 1.5, gap: 1, flexWrap: "wrap" }}
            >
              <Box sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>Movements</Box>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                {selectedType ? "Filtered — select the card again to clear" : periodLabel}
              </Box>
            </Stack>

            {entries.length === 0 ? (
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, py: 2, textAlign: "center" }}>
                No leave was credited or debited in {periodLabel}.
              </Box>
            ) : (
              <Table sx={{ width: "100%", tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCellSx, width: "7rem" }}>Date</TableCell>
                    <TableCell sx={{ ...headCellSx, width: "10rem" }}>Movement</TableCell>
                    <TableCell sx={headCellSx}>Leave type</TableCell>
                    <TableCell sx={{ ...headCellSx, width: "6rem" }} align="right">Credit</TableCell>
                    <TableCell sx={{ ...headCellSx, width: "6rem" }} align="right">Debit</TableCell>
                    <TableCell sx={{ ...headCellSx, width: "7rem" }} align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((e) => {
                    const mv = MOVEMENT[e.type] || MOVEMENT.ADJUSTMENT;
                    const numeric = { ...bodyCellSx, fontVariantNumeric: "tabular-nums" };
                    return (
                      <TableRow key={e.id} hover sx={{ "&:hover": { background: tokens.surfaceSubtle } }}>
                        <TableCell sx={{ ...numeric, whiteSpace: "nowrap" }}>{fmtDate(e.date)}</TableCell>
                        <TableCell sx={bodyCellSx}>
                          <StatusChip
                            label={mv.label}
                            color={toneColors(mv.tone).fg}
                            bg={toneColors(mv.tone).bg}
                          />
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>
                          {e.typeName || <Dash />}
                        </TableCell>
                        <TableCell
                          sx={{ ...numeric, color: e.credit ? toneColors("success").fg : tokens.border }}
                          align="right"
                        >
                          {e.credit ? num(e.credit) : "—"}
                        </TableCell>
                        <TableCell
                          sx={{ ...numeric, color: e.debit ? toneColors("danger").fg : tokens.border }}
                          align="right"
                        >
                          {e.debit ? num(e.debit) : "—"}
                        </TableCell>
                        {/* stored on the entry, not accumulated client-side, so a
                            filtered view still shows the true balance at that point */}
                        <TableCell sx={{ ...numeric, fontWeight: 600 }} align="right">
                          {num(e.balanceAfter)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Box>

          <Box
            sx={{
              border: `1px solid ${tokens.border}`,
              borderRadius: `${radius.card}px`,
              background: tokens.card,
              p: "1.25rem",
            }}
          >
            <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: 1.5 }}>Leave requests</Box>
            {requests.length === 0 ? (
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, py: 2, textAlign: "center" }}>
                This employee hasn't requested any leave.
              </Box>
            ) : (
              <Table sx={{ width: "100%", tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Type</TableCell>
                    <TableCell sx={{ ...headCellSx, width: "12rem" }}>Dates</TableCell>
                    <TableCell sx={{ ...headCellSx, width: "5rem" }} align="right">Days</TableCell>
                    <TableCell sx={{ ...headCellSx, width: "8rem" }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((r) => {
                    const first = fmtDate(r.firstDay);
                    const last = fmtDate(r.lastDay);
                    return (
                      <TableRow key={r._id} hover sx={{ "&:hover": { background: tokens.surfaceSubtle } }}>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                          {typeById[r.leaveType] || <Dash />}
                        </TableCell>
                        <TableCell
                          sx={{ ...bodyCellSx, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
                        >
                          {last && last !== first ? `${first} → ${last}` : first}
                        </TableCell>
                        <TableCell
                          sx={{ ...bodyCellSx, fontVariantNumeric: "tabular-nums" }}
                          align="right"
                        >
                          {r.days?.length || 0}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <StatusChip label={r.status} tone={REQUEST_TONE[r.status]} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ProfileLeave;
